import { useEffect, useState } from "react";
import { Check, Edit2, Loader2, MessageCircleQuestion, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useDocumentSummary } from "@/hooks/knowledge/useDocumentSummary";
import { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";
import { cn } from "@/lib/utils";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface DocumentUnderstandingProps {
  document: DocumentWithMetadata;
  onDocumentUpdate?: (
    document: DocumentWithMetadata,
    updates: Partial<DocumentWithMetadata>,
  ) => Promise<unknown>;
  readOnly?: boolean;
}

/**
 * Sits directly above the raw scraped text in the document details drawer.
 * Shows the plain-language summary, the questions the document can answer,
 * and topic tags a Genie would draw on this document for — or, when no
 * summary exists yet (the state every document starts in), an inviting
 * empty state that drafts one on demand.
 */
export const DocumentUnderstanding = ({ document, onDocumentUpdate, readOnly }: DocumentUnderstandingProps) => {
  const { summary, answers, topics, summarisedAt, isSummarising, error, summarise } = useDocumentSummary(document);

  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [editedSummary, setEditedSummary] = useState(summary ?? "");
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  useEffect(() => {
    setIsEditingSummary(false);
  }, [document.id]);

  useEffect(() => {
    if (!isEditingSummary) setEditedSummary(summary ?? "");
  }, [summary, isEditingSummary]);

  const canManage = !readOnly;
  const canEditSummary = canManage && Boolean(onDocumentUpdate) && Boolean(summary);

  const handleStartEdit = () => {
    setEditedSummary(summary ?? "");
    setIsEditingSummary(true);
  };

  const handleCancelEdit = () => {
    setEditedSummary(summary ?? "");
    setIsEditingSummary(false);
  };

  const handleSaveEdit = async () => {
    if (!onDocumentUpdate) return;
    const trimmed = editedSummary.trim();
    if (!trimmed) {
      toast.error("Summary can't be empty");
      return;
    }

    setIsSavingEdit(true);
    try {
      await onDocumentUpdate(document, {
        metadata: { summary: trimmed, description: trimmed },
      });
      setIsEditingSummary(false);
    } catch (err) {
      console.error("Failed to save edited summary:", err);
      toast.error("Failed to save your edits");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const summarisedLabel = summarisedAt
    ? new Date(summarisedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : null;

  return (
    <div className="mx-6 mt-4 rounded-xl border border-[#E5EAF0] bg-white overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#E5EAF0] bg-[#F9FAFC]">
        <div className="flex items-center gap-2.5">
          <GenieIcon
            name="brain"
            size={20}
            color="#4E9CFF"
            secondaryColor="#FF4DB8"
            gradientEnabled
            label=""
          />
          <span className="text-sm font-semibold text-gray-800">What your Genie takes from this</span>
        </div>
        {summary && !isSummarising && summarisedLabel && (
          <span className="text-xs text-gray-400 flex-shrink-0">Summarised {summarisedLabel}</span>
        )}
      </div>

      <div className="p-4">
        {isSummarising ? (
          <div className="flex items-center gap-3 py-4">
            <img src="/helpgenie_logo.png" alt="" className="h-8 w-8 flex-shrink-0 animate-gentle-pulse" />
            <div>
              <p className="text-sm font-medium text-gray-700">Reading this document and drafting a summary…</p>
              <p className="text-xs text-gray-500 mt-0.5">This takes a few seconds for most documents.</p>
            </div>
          </div>
        ) : summary ? (
          <div className="space-y-3">
            <div>
              {isEditingSummary ? (
                <div className="space-y-2">
                  <Textarea
                    value={editedSummary}
                    onChange={(e) => setEditedSummary(e.target.value)}
                    className="text-sm leading-relaxed text-gray-800 rounded-lg focus-visible:ring-genie-primary min-h-[70px]"
                    disabled={isSavingEdit}
                    autoFocus
                  />
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={handleSaveEdit}
                      disabled={isSavingEdit}
                      className="h-7 bg-gradient-to-r from-genie-primary to-genie-secondary text-white hover:opacity-90"
                    >
                      {isSavingEdit ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Check className="h-3.5 w-3.5" />
                      )}
                      <span className="ml-1">Save</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCancelEdit}
                      disabled={isSavingEdit}
                      className="h-7"
                    >
                      <X className="h-3.5 w-3.5" />
                      <span className="ml-1">Cancel</span>
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm leading-relaxed text-gray-800">{summary}</p>
                  {canManage && (
                    <div className="flex items-center gap-2 pt-3">
                      {canEditSummary && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleStartEdit}
                          className="h-8 gap-1.5 text-xs font-medium border-2 border-gray-300 text-gray-700 hover:bg-gray-100 hover:border-gray-400"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                          Edit summary
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={summarise}
                        className="h-8 gap-1.5 text-xs font-medium border-2 border-gray-300 text-gray-700 hover:bg-gray-100 hover:border-gray-400"
                        title="Draft a fresh summary"
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                        Summarise again
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>

            {topics.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {topics.map((topic) => (
                  <span
                    key={topic}
                    className="px-2 py-0.5 rounded-full text-xs font-medium bg-genie-primary/10 text-genie-primary"
                  >
                    {topic}
                  </span>
                ))}
              </div>
            )}

            {answers.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Questions this answers
                </p>
                <ul className="space-y-1">
                  {answers.map((question) => (
                    <li key={question} className="flex items-start gap-2 text-sm text-gray-700">
                      <MessageCircleQuestion className="h-3.5 w-3.5 text-genie-secondary flex-shrink-0 mt-0.5" />
                      <span>{question}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : error ? (
          <div className="flex flex-col items-start gap-2 py-2">
            <p className="text-sm text-red-600">{error}</p>
            {canManage && (
              <Button
                size="sm"
                variant="outline"
                onClick={summarise}
                className="h-8 gap-1.5 border-red-200 text-red-600 hover:bg-red-50"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Try again
              </Button>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-between gap-4 py-1">
            <p className="text-sm text-gray-500">
              {canManage
                ? "This document hasn't been summarised yet — your Genie is reading the raw text below."
                : "No summary added yet."}
            </p>
            {canManage && (
              <Button
                size="sm"
                onClick={summarise}
                className={cn(
                  "h-8 flex-shrink-0 gap-1.5 text-white shadow-sm",
                  "bg-gradient-to-r from-genie-primary to-genie-secondary hover:opacity-90",
                )}
              >
                Summarise this document
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentUnderstanding;
